import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, type OnChanges, type OnInit, type SimpleChanges } from '@angular/core';
import { BasisCostCenterService } from '@zaoblako/web/core/services/basis/basis-cost-center.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { StandardSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/standard-dropdown/standard-single-select/standard-single-select.component';
import type { CostCenterFindInterface } from '@zaoblako/shared/modules/catalog/dtos/cost-center/cost-center-find.dto';

import { isNil } from 'lodash';

/**
 * @deprecated Use `crm-standard-single-select` and `BasisCostCenterService.dropdownDescriptorApi`.
 */
@Component({
  selector: 'crm-cost-center-single-select',
  standalone: true,
  imports: [SingleSelectComponent, StandardSingleSelectComponent],
  templateUrl: './cost-center-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CostCenterSingleSelectComponent implements OnInit, OnChanges {
  @Input() public costCenters: CostCenterFindInterface[] = [];
  @Input() public selectedCostCenter: CostCenterFindInterface;
  @Output() public selectedCostCenterChange = new EventEmitter<CostCenterFindInterface>();
  @Input() public customSearch = false;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = true;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public appendTo: string;
  @Input() public labelInput = false;

  /** Set only after `setSelectedCostCenterId`. `undefined` means the filter input is not used. */
  public costCenterId: number | null | undefined = undefined;
  public loading = false;
  public selectionLoading = false;
  public useStandard = false;
  public readonly descriptorUrl: string;
  private inputsReady = false;

  public constructor(
    basisCostCenterService: BasisCostCenterService,
    private readonly globalService: GlobalService
  ) {
    this.descriptorUrl = basisCostCenterService.dropdownDescriptorApi;
  }

  @Input() public set setSelectedCostCenterId(id: number | string | null | undefined) {
    if (isNil(id) || id === '') {
      this.costCenterId = null;
    } else {
      this.costCenterId = typeof id === 'string' ? Number(id) : id;
    }
    if (this.inputsReady && !this.useStandard) {
      this.applyCustomSelection();
    }
  }

  public ngOnInit(): void {
    this.useStandard = !this.customSearch && !!this.globalService.globalInfo?.tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled;
    this.inputsReady = true;
    if (!this.useStandard) {
      this.applyCustomSelection();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.useStandard && changes.costCenters && !changes.costCenters.isFirstChange()) {
      this.applyCustomSelection();
    }
  }

  public onSelected = (costCenter: CostCenterFindInterface): void => {
    this.selectedCostCenter = costCenter;
    this.selectedCostCenterChange.emit(costCenter);
  };

  private applyCustomSelection = (): void => {
    if (this.costCenterId === undefined) {
      this.selectionLoading = false;
      return;
    }
    if (isNil(this.costCenterId)) {
      this.selectionLoading = false;
      return;
    }

    this.selectedCostCenter = this.costCenters.find((costCenter) => costCenter.id === this.costCenterId);
    this.selectionLoading = false;
  };
}
